import { useRef, type ReactNode } from "react";
import { animate, motion, useMotionValue, useTransform, type MotionValue, type PanInfo } from "motion/react";
import type { Decision, Job } from "../../shared/types.ts";
import { Chip, Monogram, SourceLogo, cn, sourceOf } from "../ui/index.ts";
import { ago, pay, place, SENIORITY, visaNote, WORKPLACE } from "../lib/format.ts";
import { FitPanel } from "./Fit.tsx";
import { ghostRisk, MatchChip } from "./parts.tsx";

type Dir = 1 | -1 | 0;
/** How the deck is moving: `dir` for the card leaving (`gone`), `back` for a card an undo brings back. */
export type Motion = { dir: Dir; back: Dir | null; gone: string | null };

export const dirOf = (d: Decision): Dir => (d === "apply" ? 1 : d === "pass" ? -1 : 0);

/** The description snippet without a leading copy of the title or a "Company:" line, which many postings open with. */
export function lede(job: Pick<Job, "title" | "descriptionSnippet">): string | null {
  let t = job.descriptionSnippet?.trim() ?? "";
  if (t.toLowerCase().startsWith(job.title.toLowerCase())) t = t.slice(job.title.length).trim();
  t = t.replace(/^(company|location|about( the)? (company|role|us)|job description|overview)\s*:\s*/i, "").trim();
  return t || null;
}

/** The ATS a job's form lives on: its real logo and name, or just the name for a source without one. */
export function Via({ source, className }: { source: string; className?: string }) {
  if (!sourceOf(source)) return <span className={cn("text-[13px] text-muted", className)}>{source}</span>;
  return <SourceLogo source={source} size="xs" withName className={cn("gap-1.5 text-[13px] font-medium text-muted", className)} />;
}

/** The tinted flags on a job: rank, visa stance, ghost risk and whether Headcount can send it. */
export function JobFlags({ job, className }: { job: Job; className?: string }) {
  const v = visaNote(job.visa);
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      <MatchChip score={job.score} />
      {job.canAutoApply ? <Chip tone="ok">Auto-apply</Chip> : <Chip tone="outline">Company site</Chip>}
      {v && <Chip tone={v.tone}>{v.text}</Chip>}
      {ghostRisk(job.ghost)?.label === "High" && <Chip tone="bad">Ghost risk</Chip>}
    </div>
  );
}

const THRESHOLD = 110;
const settle = { type: "spring" as const, stiffness: 340, damping: 26 };

/** One card of the swipe deck. The card behind the top one is a `peek`: smaller, lower, still. */
export function SwipeCard({
  job,
  custom,
  peek = false,
  onDecide,
  onOpen,
}: {
  job: Job;
  custom: Motion;
  peek?: boolean;
  onDecide: (d: Decision, j: Job) => void;
  onOpen: (j: Job) => void;
}) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-16, 16]);
  const applyOpacity = useTransform(x, [20, THRESHOLD], [0, 1]);
  const passOpacity = useTransform(x, [-THRESHOLD, -20], [1, 0]);
  const saveOpacity = useTransform(y, [-THRESHOLD, -20], [1, 0]);
  const dragged = useRef(false);

  const variants = {
    enter: ({ back }: Motion) =>
      back === null ? { opacity: 0, scale: 0.86, y: 44 } : back === 0 ? { opacity: 0, y: -260, scale: 0.9 } : { opacity: 0, x: back * 460, rotate: back * 18 },
    center: { opacity: 1, x: 0, y: 0, scale: 1, rotate: 0, zIndex: 2, transition: settle },
    peek: { opacity: 1, x: 0, y: 16, scale: 0.96, rotate: 0, zIndex: 1, transition: settle },
    exit: ({ dir, gone }: Motion) =>
      gone !== job.id
        ? { opacity: 0, scale: 0.86, y: 44, zIndex: 0, transition: { duration: 0.2 } }
        : dir === 0
          ? { opacity: 0, y: -520, scale: 0.9, rotate: -4, zIndex: 3, transition: { type: "spring" as const, stiffness: 220, damping: 24 } }
          : { opacity: 0, x: dir * 760, y: 40, rotate: dir * 28, zIndex: 3, transition: { type: "spring" as const, stiffness: 200, damping: 24 } },
  };

  const end = (_: unknown, info: PanInfo) => {
    const { x: dx, y: dy } = info.offset;
    const vx = info.velocity.x;
    const fling = Math.abs(vx) > 500 && Math.abs(dx) > 40 && Math.sign(vx) === Math.sign(dx);
    if (dy < -THRESHOLD && Math.abs(dy) > Math.abs(dx)) onDecide("save", job);
    else if (Math.abs(dx) > THRESHOLD || fling) onDecide(dx > 0 ? "apply" : "pass", job);
    else {
      animate(x, 0, settle);
      animate(y, 0, settle);
    }
    setTimeout(() => (dragged.current = false));
  };

  const money = pay(job);
  const snippet = lede(job);
  const ghost = ghostRisk(job.ghost);
  const facts: [string, ReactNode][] = [
    ["Pay", money ? <span className="tabular-nums">{money}</span> : null],
    ["Workplace", WORKPLACE[job.workplace] ?? null],
    ["Level", job.seniority ? (SENIORITY[job.seniority] ?? null) : null],
    ["Ghost risk", ghost ? <span className={cn(ghost.tone === "red" && "text-bad-ink", ghost.tone === "amber" && "text-warn-ink")}>{ghost.label}</span> : null],
    ["Posted", job.postedAt ? ago(job.postedAt) : null],
    ["Applies via", <Via source={job.source} className="text-sm font-semibold text-ink" />],
  ];

  return (
    <motion.article
      custom={custom}
      variants={variants}
      initial="enter"
      animate={peek ? "peek" : "center"}
      exit="exit"
      style={{ x, y, rotate, transformOrigin: "50% 90%" }}
      drag={!peek}
      dragMomentum={false}
      onDragStart={() => (dragged.current = true)}
      onDragEnd={end}
      whileDrag={{ scale: 1.02, cursor: "grabbing" }}
      aria-hidden={peek || undefined}
      aria-label={peek ? undefined : `${job.title} at ${job.company}`}
      className={cn(
        "relative flex min-h-[340px] select-none flex-col overflow-hidden rounded-xl bg-surface shadow-raised [grid-area:1/1] md:min-h-[420px]",
        peek ? "pointer-events-none" : "cursor-grab touch-none",
      )}
    >
      <Stamp opacity={applyOpacity} className="left-5 top-5 -rotate-6 bg-tone-mint text-tone-mint-ink ring-ok">
        {job.canAutoApply ? "Apply" : "Apply on site"}
      </Stamp>
      <Stamp opacity={passOpacity} className="right-5 top-5 rotate-6 bg-tone-red text-tone-red-ink ring-bad">
        Pass
      </Stamp>
      <Stamp opacity={saveOpacity} className="bottom-6 left-1/2 -translate-x-1/2 bg-tone-cream text-tone-cream-ink ring-accent">
        Save
      </Stamp>
      <div className="flex flex-col gap-4 p-5 md:p-7">
        <div className="flex items-center gap-3.5">
          <Monogram name={job.company} url={job.jobUrl} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-bold tracking-[-0.01em] text-ink">{job.company}</p>
            <p className="truncate text-[13px] text-muted">{place(job)}</p>
          </div>
        </div>
        <button
          type="button"
          tabIndex={peek ? -1 : 0}
          onClick={() => !dragged.current && onOpen(job)}
          className="-my-1 line-clamp-3 py-1 text-left font-display text-[26px] leading-[1.1] text-balance text-ink decoration-primary decoration-[3px] underline-offset-[6px] hover:underline md:text-[28px]"
        >
          {job.title}
        </button>
        <JobFlags job={job} />
      </div>
      <dl className="grid grid-cols-3 gap-px border-y border-line bg-line">
        {facts.map(([label, value]) => (
          <div key={label} className="min-w-0 bg-surface px-4 py-2.5 md:px-7 md:py-3">
            <dt className="truncate text-xs font-semibold text-muted">{label}</dt>
            <dd className="mt-0.5 truncate text-sm font-semibold text-ink">{value ?? <span className="font-medium text-muted">Not listed</span>}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-1 flex-col gap-4 p-5 md:px-7 md:py-6">
        {snippet && (
          <p className="hidden max-h-[4.5rem] overflow-hidden text-sm leading-relaxed text-ink-soft text-pretty [mask-image:linear-gradient(to_bottom,black_2.5rem,transparent_4.5rem)] sm:block">
            {snippet}
          </p>
        )}
        <div className="mt-auto flex flex-col" onPointerDownCapture={(e) => e.stopPropagation()}>
          {!peek && <FitPanel jobId={job.id} />}
        </div>
      </div>
    </motion.article>
  );
}

function Stamp({ opacity, className, children }: { opacity: MotionValue<number>; className: string; children: React.ReactNode }) {
  const scale = useTransform(opacity, [0, 1], [0.7, 1]);
  return (
    <motion.span
      aria-hidden
      style={{ opacity, scale }}
      className={cn("pointer-events-none absolute z-10 rounded-control px-3.5 py-1.5 font-display text-xl ring-2", className)}
    >
      {children}
    </motion.span>
  );
}
